/**
 * Server-quality PDF generation via `@react-pdf/renderer`.
 */

import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { getPdfTemplateEntry } from './templates/registry';
import { openPdfPrintPreview } from './pdfPrintPreviewApi';

/**
 * @param {string} templateId registry key (see PDF_TEMPLATE_IDS)
 * @param {Record<string, unknown>} props forwarded to the template component (e.g. `{ data }`)
 * @param {{ scale?: number }} [options]
 * @returns {Promise<Blob>}
 */
export async function renderPdfTemplateToBlob(templateId, props, options = {}) {
    const entry = getPdfTemplateEntry(templateId);
    if (!entry) {
        throw new Error(`Unknown PDF template: ${templateId}`);
    }
    const scale = options.scale ?? 1;
    const element = React.createElement(entry.component, { ...props, scale });
    return pdf(element).toBlob();
}

/**
 * @param {string} templateId
 * @param {Record<string, unknown>} props
 * @param {{ scale?: number }} [options]
 * @returns {Promise<ArrayBuffer>}
 */
export async function renderPdfTemplateToArrayBuffer(templateId, props, options = {}) {
    const blob = await renderPdfTemplateToBlob(templateId, props, options);
    return blob.arrayBuffer();
}

/**
 * Trigger a browser download (optional helper for manual testing).
 * @param {string} templateId
 * @param {Record<string, unknown>} props
 * @param {string} [filename]
 * @param {{ scale?: number }} [options]
 */
/**
 * Trigger a browser download for an existing PDF blob.
 * @param {Blob} blob
 * @param {string} [filename]
 */
export function downloadPdfBlob(blob, filename = 'sicily-document.pdf') {
    const url = URL.createObjectURL(blob);
    try {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } finally {
        URL.revokeObjectURL(url);
    }
}

export async function downloadPdfTemplate(templateId, props, filename = 'sicily-document.pdf', options = {}) {
    const blob = await renderPdfTemplateToBlob(templateId, props, options);
    downloadPdfBlob(blob, filename);
}

/**
 * Open generated PDF in a new tab and trigger the browser print dialog.
 * @param {Blob} blob
 */
export async function printPdfBlob(blob) {
    const url = URL.createObjectURL(blob);
    const printWin = window.open(url, '_blank', 'noopener,noreferrer');
    if (!printWin) {
        URL.revokeObjectURL(url);
        throw new Error('Pop-up blocked — allow pop-ups for this site to print.');
    }

    let triggered = false;
    const runPrint = () => {
        if (triggered || printWin.closed) return;
        triggered = true;
        try {
            printWin.focus();
            printWin.print();
        } catch {
            /* viewer may still offer Print from its UI */
        }
        setTimeout(() => {
            try {
                URL.revokeObjectURL(url);
            } catch {
                /* ignore */
            }
        }, 120_000);
    };

    printWin.addEventListener('load', () => setTimeout(runPrint, 400), { once: true });
    setTimeout(runPrint, 2500);
}

/**
 * Open print preview (scale slider), then print when confirmed.
 * @param {string} templateId
 * @param {Record<string, unknown>} props
 * @param {{ title?: string }} [options]
 */
export async function printPdfTemplate(templateId, props, options = {}) {
    try {
        await openPdfPrintPreview({
            templateId,
            props,
            title: options.title,
        });
    } catch (e) {
        if (e?.code === 'PRINT_CANCELLED') return;
        throw e;
    }
}
